// src/oura/merchant/setup/SendOn.tsx
//
// "Someone else looks after this?" under a step: send just that step to the
// person who can do it, by email, with no login needed. Access often sits
// with a web agency or an IT person, not the owner who signed.
//
// A button first. The form opens on request, so an owner who does it
// themselves never reads it.
//
// Sending one step ships with the in-platform questions (one backend
// change, read here as `form.inPlatform`). Until then the route sends every
// access step, as the old page did, so the copy says that and the questions
// are not offered.

import { useEffect, useId, useState, type FormEvent } from "react";
import { SendIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { PillButton, TYPE } from "@oura/ui";
import { shareMySteps, type MerchantOnboarding } from "@service/aeo/onboarding";
import { isEmail } from "@oura/onboarding/drafts";
import { dayMonth } from "@oura/onboarding/derive";
import type { MerchantStep } from "@oura/onboarding/oneSetup";
import { AccessField } from "../AccessDrawer";

const TAP = "max-sm:h-9";

type SendOnProps = {
    step: MerchantStep;
    onboarding: MerchantOnboarding;
    readOnly: boolean;
    onChanged: (updated: MerchantOnboarding) => void;
};

/** Fresh for each step: a name typed for one step never carries into the next. */
export function SendOn(props: SendOnProps) {
    return <SendOnForStep key={props.step.key} {...props} />;
}

function SendOnForStep({ step, onboarding, readOnly, onChanged }: SendOnProps) {
    const [open, setOpen] = useState(false);
    // The person the old form named for access, when it gave an address. Never for the questions: those are the owner's.
    const contact = step.key !== "form" && onboarding.accessContact?.email ? onboarding.accessContact : null;
    const [name, setName] = useState(contact?.name ?? "");
    const [email, setEmail] = useState(contact?.email ?? "");
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [failure, setFailure] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const id = useId();
    const nameId = `${id}-name`;
    const emailId = `${id}-email`;

    // Focus the first field when the form opens on a press.
    useEffect(() => {
        if (open) document.getElementById(nameId)?.focus();
    }, [open, nameId]);

    const perStep = onboarding.form?.inPlatform === true;
    // The business questions are the owner's to answer: only the access steps go to someone else.
    if (step.key === "form") return null;

    const last = onboarding.sharedWith.find((p) => (p.steps ? p.steps.some((k) => step.shareKeys.includes(k)) : step.key !== "form"));

    const send = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const found: { name?: string; email?: string } = {};
        if (!name.trim()) found.name = "Add their name.";
        if (!isEmail(email)) found.email = email.trim() ? "Check the email address." : "Add their email.";
        setErrors(found);
        if (found.name || found.email) {
            document.getElementById(found.name ? nameId : emailId)?.focus();
            return;
        }
        setBusy(true);
        setFailure(null);
        try {
            onChanged(await shareMySteps({ name: name.trim(), email: email.trim() }, perStep ? step.shareKeys : undefined));
            setOpen(false);
        } catch {
            setFailure("It was not sent. Try again in a moment.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            {last && !open && (
                <p className={cn(TYPE.tc, "m-0 mb-2 text-gray-500")}>
                    Sent to {last.name}
                    {dayMonth(last.at) ? ` on ${dayMonth(last.at)}` : ""}. We tick this off once their invite reaches us.
                </p>
            )}
            {!open ? (
                <PillButton variant="text" icon={SendIcon} onClick={() => setOpen(true)}>
                    {last ? "Send again" : "Someone else looks after this?"}
                </PillButton>
            ) : (
                <form noValidate onSubmit={(e) => void send(e)} className="rounded-2xl bg-gray-50 p-4">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{perStep ? "Send them this step" : "Send them the access steps"}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                        {perStep ? "They get an email with just this step, from oura@ourteam.ai, with you in copy." : "They get an email with every access step, with you in copy."} They do not need a login.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <AccessField
                            id={nameId}
                            label="Their name"
                            autoComplete="off"
                            value={name}
                            error={errors.name}
                            onChange={(v) => {
                                setName(v);
                                if (errors.name) setErrors((was) => ({ ...was, name: undefined }));
                            }}
                        />
                        <AccessField
                            id={emailId}
                            label="Their email"
                            type="email"
                            autoComplete="off"
                            value={email}
                            error={errors.email}
                            onChange={(v) => {
                                setEmail(v);
                                if (errors.email) setErrors((was) => ({ ...was, email: undefined }));
                            }}
                        />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <PillButton type="submit" variant="primary" busy={busy} disabled={readOnly} className={TAP}>
                            Send
                        </PillButton>
                        <PillButton className={TAP} onClick={() => setOpen(false)}>
                            Cancel
                        </PillButton>
                    </div>
                    {failure && (
                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>
                            {failure}
                        </p>
                    )}
                </form>
            )}
        </div>
    );
}
